import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { COUNTRIES_DATA, COUNTRIES, PROFESSIONS } from '../data/locations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubmitProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    date_of_birth: '',
    age: '',
    profession: '',
    other_profession: '',
    experience: '',
    country: '',
    state: '',
    city: '',
    address: '',
    gender: '',
    skills: '',
    summary: '',
  });

  const [cvFile, setCvFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [portfolioFile, setPortfolioFile] = useState(null);

  const states = formData.country ? COUNTRIES_DATA[formData.country] || [] : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') {
      setFormData(prev => ({ ...prev, country: value, state: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!cvFile) throw new Error('CV file is required');
      if (!cvFile.name.toLowerCase().endsWith('.pdf')) throw new Error('CV must be a PDF file');
      if (cvFile.size > 10 * 1024 * 1024) throw new Error('CV file must be under 10MB');
      if (!photoFile) throw new Error('Passport photograph is required');

      let result = null;

      // Try two-step approach first (handles large files better)
      try {
        const profileRes = await axios.post(`${API_URL}/api/profiles/submit-info`, {
          full_name: formData.full_name,
          phone: formData.phone,
          email: formData.email,
          date_of_birth: formData.date_of_birth,
          age: formData.age,
          profession: formData.profession,
          other_profession: formData.other_profession || '',
          experience: formData.experience,
          country: formData.country,
          state: formData.state,
          city: '',
          address: formData.address,
          gender: formData.gender,
          skills: formData.skills || '',
          summary: formData.summary || '',
        });

        const uniqueId = profileRes.data.unique_id;

        // Upload CV
        const cvData = new FormData();
        cvData.append('cv_file', cvFile);
        await axios.post(`${API_URL}/api/profiles/upload-cv/${uniqueId}`, cvData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });

        // Upload Photo
        const photoData = new FormData();
        photoData.append('photo', photoFile);
        await axios.post(`${API_URL}/api/profiles/upload-photo/${uniqueId}`, photoData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });

        // Upload Portfolio (optional)
        if (portfolioFile) {
          const portData = new FormData();
          portData.append('portfolio', portfolioFile);
          await axios.post(`${API_URL}/api/profiles/upload-portfolio/${uniqueId}`, portData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
          });
        }

        result = profileRes.data;
      } catch (twoStepErr) {
        // Fallback: single multipart request (backward compatible)
        const data = new FormData();
        data.append('full_name', formData.full_name);
        data.append('phone', formData.phone);
        data.append('email', formData.email);
        data.append('date_of_birth', formData.date_of_birth);
        data.append('age', formData.age);
        data.append('profession', formData.profession);
        data.append('other_profession', formData.other_profession || '');
        data.append('experience', formData.experience);
        data.append('country', formData.country);
        data.append('state', formData.state);
        data.append('city', '');
        data.append('address', formData.address);
        data.append('gender', formData.gender);
        data.append('skills', formData.skills || '');
        data.append('summary', formData.summary || '');
        data.append('cv_file', cvFile);
        data.append('passport_photo', photoFile);
        if (portfolioFile) data.append('portfolio_file', portfolioFile);

        const response = await axios.post(`${API_URL}/api/profiles/submit`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        result = response.data;
      }

      setResult(result);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      } else if (err.code === 'ERR_NETWORK') {
        setError('Submission failed. Please try again or use a smaller CV file (under 5MB).');
      } else {
        setError(err.message || 'Submission failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] text-slate-200 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full bg-[#0E1525] border border-slate-800 p-10 text-center space-y-6 rounded-xl"
        >
          <CheckCircle2 className="h-14 w-14 text-green-400 mx-auto" strokeWidth={1.5} />
          <h1 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-100">Submission Received Successfully</h1>
          <p className="text-slate-300">Your profile has been added to the BuildDirect professional database.</p>
          <p className="text-slate-400 text-sm">You may be contacted when relevant opportunities match your profile.</p>
          <div className="bg-green-900/20 border-2 border-green-500/60 p-6 space-y-3 rounded">
            <div className="text-xs uppercase tracking-widest text-green-400 font-bold">IMPORTANT - SAVE YOUR UNIQUE ID</div>
            <div className="text-2xl font-bold text-green-300 font-mono tracking-wider" data-testid="submission-unique-id">
              {result?.unique_id}
            </div>
            <div className="text-slate-300 font-semibold text-sm">
              Please write down, screenshot, or copy this Unique ID and keep it safe.
            </div>
            <div className="text-xs text-slate-400" data-testid="submission-email">Email: {result?.email}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <button
              onClick={() => navigate('/retrieve')}
              data-testid="goto-retrieve-button"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-xs uppercase tracking-wider font-semibold transition-colors rounded"
            >
              Retrieve / Update Profile
            </button>
            <button
              onClick={() => navigate('/')}
              data-testid="goto-home-button"
              className="border border-slate-700 hover:border-slate-500 hover:bg-slate-800/40 text-slate-200 px-6 py-3 text-xs uppercase tracking-wider font-semibold transition-colors rounded"
            >
              Back to Home
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const inputClass = "w-full bg-slate-900/40 border border-slate-700 text-slate-100 h-11 px-4 text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded transition-colors";
  const labelClass = "text-xs uppercase tracking-widest text-slate-400 font-semibold mb-1.5 block";

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-slate-200">
      <header className="sticky top-0 z-50 bg-[#0B0F1A]/90 backdrop-blur-xl border-b border-slate-800/60 h-16 flex items-center justify-center px-6">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <img 
            src="https://customer-assets.emergentagent.com/job_submission-hub-21/artifacts/1pp9pqr0_1000260687.png" 
            alt="BuildDirect Logo" 
            className="h-10"
          />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl tracking-tight mb-2 text-slate-100">Submit Your Profile</h1>
          <p className="text-slate-400 text-sm">Complete the form below. It takes less than 2 minutes.</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-900/20 border border-red-500/40 p-3 flex items-start gap-2 rounded" data-testid="error-message">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Info */}
          <div className="space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">
              Personal Information
            </h2>
            <div>
              <label className={labelClass}>Full Name *</label>
              <input type="text" name="full_name" required value={formData.full_name} onChange={handleChange}
                data-testid="input-full-name" className={inputClass} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Phone Number *</label>
                <input type="tel" name="phone" required value={formData.phone} onChange={handleChange}
                  data-testid="input-phone" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Email Address *</label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange}
                  data-testid="input-email" className={inputClass} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Gender *</label>
              <select name="gender" required value={formData.gender} onChange={handleChange}
                data-testid="select-gender" className={inputClass}>
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Date of Birth *</label>
                <input type="date" name="date_of_birth" required value={formData.date_of_birth} onChange={handleChange}
                  data-testid="input-dob" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Age *</label>
                <input type="number" name="age" required min="16" max="80" value={formData.age} onChange={handleChange}
                  placeholder="e.g. 32" data-testid="input-age" className={inputClass} />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">
              Location
            </h2>
            <div>
              <label className={labelClass}>Country *</label>
              <select name="country" required value={formData.country} onChange={handleChange}
                data-testid="select-country" className={inputClass}>
                <option value="">Select Country</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>State / Region *</label>
              <select name="state" required value={formData.state} onChange={handleChange}
                data-testid="select-state" className={inputClass} disabled={!formData.country}>
                <option value="">{formData.country ? 'Select State / Region' : 'Select country first'}</option>
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Home Address *</label>
              <input type="text" name="address" required value={formData.address} onChange={handleChange}
                placeholder="Enter your full home address" data-testid="input-address" className={inputClass} />
            </div>
          </div>

          {/* Professional */}
          <div className="space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">
              Professional Details
            </h2>
            <div>
              <label className={labelClass}>Profession *</label>
              <select name="profession" required value={formData.profession} onChange={handleChange}
                data-testid="select-profession" className={inputClass}>
                <option value="">Select Profession</option>
                {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            {formData.profession === 'Other' && (
              <div>
                <label className={labelClass}>Please Specify</label>
                <input type="text" name="other_profession" value={formData.other_profession} onChange={handleChange}
                  data-testid="input-other-profession" className={inputClass} />
              </div>
            )}
            <div>
              <label className={labelClass}>Years of Experience *</label>
              <input type="text" name="experience" required placeholder="e.g. 5 years" value={formData.experience} onChange={handleChange}
                data-testid="input-experience" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Skills <span className="normal-case text-slate-400">(optional)</span></label>
              <input type="text" name="skills" value={formData.skills} onChange={handleChange}
                placeholder="e.g. AutoCAD, Structural Design, Project Management"
                data-testid="input-skills" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Short Summary <span className="normal-case text-slate-400">(optional, 2-3 lines)</span></label>
              <textarea name="summary" value={formData.summary} onChange={handleChange} rows={2}
                placeholder="Brief overview of your background and expertise"
                data-testid="textarea-summary"
                className="w-full bg-slate-900/40 border border-slate-700 text-slate-100 px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded transition-colors" />
            </div>
          </div>

          {/* Uploads */}
          <div className="space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">
              Upload Documents
            </h2>
            <div>
              <label className={labelClass}>Passport Photograph *</label>
              <div className="border border-dashed border-slate-700 hover:border-blue-500/50 rounded p-5 text-center bg-slate-900/30 transition-colors">
                <Upload className="h-6 w-6 text-slate-500 mx-auto mb-2" />
                <input
                  type="file"
                  name="passport_photo"
                  accept="image/*"
                  required
                  onChange={(e) => e.target.files[0] && setPhotoFile(e.target.files[0])}
                  data-testid="upload-passport-photo"
                  className="text-sm text-slate-400 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700"
                />
                {photoFile && <p className="text-xs text-green-400 mt-2">{photoFile.name}</p>}
              </div>
            </div>
            <div>
              <label className={labelClass}>CV (PDF Only) *</label>
              <div className="border border-dashed border-slate-700 hover:border-blue-500/50 rounded p-5 text-center bg-slate-900/30 transition-colors">
                <Upload className="h-6 w-6 text-slate-500 mx-auto mb-2" />
                <input
                  type="file"
                  name="cv_file"
                  accept=".pdf"
                  required
                  onChange={(e) => e.target.files[0] && setCvFile(e.target.files[0])}
                  data-testid="upload-cv-file"
                  className="text-sm text-slate-400 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700"
                />
                {cvFile && <p className="text-xs text-green-400 mt-2">{cvFile.name}</p>}
              </div>
            </div>
            <div>
              <label className={labelClass}>Portfolio <span className="normal-case text-slate-500">(optional - PDF or Images)</span></label>
              <div className="border border-dashed border-slate-700 hover:border-blue-500/50 rounded p-5 text-center bg-slate-900/30 transition-colors">
                <Upload className="h-6 w-6 text-slate-500 mx-auto mb-2" />
                <input
                  type="file"
                  name="portfolio_file"
                  accept=".pdf,image/*"
                  onChange={(e) => e.target.files[0] && setPortfolioFile(e.target.files[0])}
                  data-testid="upload-portfolio-file"
                  className="text-sm text-slate-400 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700"
                />
                {portfolioFile && <p className="text-xs text-green-400 mt-2">{portfolioFile.name}</p>}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="submit-form-button"
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white py-3.5 text-xs uppercase tracking-wider font-semibold transition-colors rounded shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40"
          >
            {loading ? 'Submitting...' : 'Submit Profile'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SubmitProfile;
